import { useState, useRef, useCallback, DragEvent, ChangeEvent } from 'react';
import { ArrowUpTrayIcon, ArrowDownTrayIcon, DocumentIcon } from '@heroicons/react/24/outline';
import { Spinner } from '../ui';

export interface CsvUploadZoneProps {
	onFile: (file: File) => void;
	statusMessage: string | null;
	statusType: 'info' | 'success' | 'error' | 'warning' | null;
	label?: string;
	description?: string;
	onDownloadTemplate: () => void;
	isDownloading?: boolean;
	formatGuideItems?: string[];
}

const CsvUploadZone = ({
	onFile,
	statusMessage,
	statusType,
	label = 'CSV Upload',
	description,
	onDownloadTemplate,
	isDownloading = false,
	formatGuideItems,
}: CsvUploadZoneProps) => {
	const [isDragOver, setIsDragOver] = useState(false);
	const inputRef = useRef<HTMLInputElement>(null);

	const handleFile = useCallback(
		(file: File) => {
			if (!file.name.toLowerCase().endsWith('.csv')) return;
			onFile(file);
		},
		[onFile]
	);

	const handleFileChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			const file = e.target.files?.[0];
			if (file) handleFile(file);
			e.target.value = '';
		},
		[handleFile]
	);

	const preventDefaults = useCallback((e: DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
	}, []);

	const handleDragEnter = useCallback(
		(e: DragEvent) => {
			preventDefaults(e);
			setIsDragOver(true);
		},
		[preventDefaults]
	);

	const handleDragLeave = useCallback(
		(e: DragEvent) => {
			preventDefaults(e);
			setIsDragOver(false);
		},
		[preventDefaults]
	);

	const handleDrop = useCallback(
		(e: DragEvent) => {
			preventDefaults(e);
			setIsDragOver(false);
			const file = e.dataTransfer?.files?.[0];
			if (file) handleFile(file);
		},
		[preventDefaults, handleFile]
	);

	return (
		<div className="csv-upload">
			{/* Header */}
			<div className="csv-upload__header">
				<span className="csv-upload__label">
					<ArrowUpTrayIcon className="w-5 h-5" />
					{label}
				</span>
				<button
					type="button"
					onClick={onDownloadTemplate}
					className="csv-upload__template-btn"
					disabled={isDownloading}
				>
					{isDownloading ? (
						<Spinner size="sm" />
					) : (
						<ArrowDownTrayIcon className="w-4 h-4" />
					)}
					{isDownloading ? 'Downloading...' : 'Download Template'}
				</button>
			</div>

			{/* Description */}
			{description && <p className="csv-upload__description">{description}</p>}

			{/* Drop Zone */}
			<div
				className={`csv-upload__dropzone ${isDragOver ? 'csv-upload__dropzone--active' : ''}`}
				onClick={() => inputRef.current?.click()}
				onDragEnter={handleDragEnter}
				onDragOver={handleDragEnter}
				onDragLeave={handleDragLeave}
				onDrop={handleDrop}
			>
				<DocumentIcon className="csv-upload__dropzone-icon" />
				<div className="text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">
					Drop CSV file here or click to browse
				</div>
				<span className="csv-upload__dropzone-hint">Accepted format: .csv</span>
			</div>

			<input
				ref={inputRef}
				type="file"
				accept=".csv"
				className="hidden"
				onChange={handleFileChange}
			/>

			{/* Status Message */}
			{statusMessage && statusType && (
				<div className={`csv-upload__status csv-upload__status--${statusType}`}>
					{statusMessage}
				</div>
			)}

			{/* Format Guide */}
			{formatGuideItems && formatGuideItems.length > 0 && (
				<div className="csv-upload__guide">
					<h4>CSV Format Guide</h4>
					<ul>
						{formatGuideItems.map((item, idx) => (
							<li key={idx}>{item}</li>
						))}
					</ul>
				</div>
			)}
		</div>
	);
};

export default CsvUploadZone;
